import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import GitHubIcon from "@material-ui/icons/GitHub";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import React from "react";
import "./FadeIn.css";

const useStyles = makeStyles((theme) => ({
  aboutContainer: {
    backgroundColor: "#0A0A0A",
    width: "100%",
    minHeight: "80vh",
    paddingTop: "100px",
    paddingBottom: "100px",
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontWeight: 700,
    marginBottom: "60px",
    position: "relative",
    display: "inline-block",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: "-12px",
      left: 0,
      width: "60px",
      height: "4px",
      background: "#00FF41",
      borderRadius: "2px",
    },
  },
  headshotContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  headshotPic: {
    maxHeight: "350px",
    maxWidth: "280px",
    borderRadius: "16px",
    border: "3px solid #1E1E1E",
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#00FF41",
      boxShadow: "0 25px 50px rgba(0, 255, 65, 0.2)",
      transform: "translateY(-5px)",
    },
  },
  aboutCard: {
    background: "rgba(20, 20, 20, 0.8)",
    backdropFilter: "blur(10px)",
    border: "1px solid #2A2A2A",
    borderRadius: "16px",
    padding: "32px",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "rgba(0, 255, 65, 0.3)",
    },
  },
  bioText: {
    color: "#FFFFFF",
    fontSize: "1rem",
    lineHeight: 1.8,
    marginBottom: "16px",
  },
  lineBreak: {
    backgroundColor: "#2A2A2A",
    height: "1px",
    borderWidth: 0,
    marginTop: "24px",
    marginBottom: "24px",
  },
  detailLabel: {
    color: "#B0B0B0",
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  detailValue: {
    color: "#FFFFFF",
    fontSize: "0.9rem",
  },
  emailLink: {
    color: "#00FF41",
    textDecoration: "none",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#FFFFFF",
    },
  },
  iconContainer: {
    display: "flex",
    gap: "8px",
    marginTop: "8px",
  },
  icon: {
    fontSize: 40,
    color: "#B0B0B0",
    transition: "all 0.3s ease",
    padding: "8px",
    borderRadius: "8px",
    "&:hover": {
      color: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.1)",
      transform: "translateY(-2px)",
    },
  },
  contactLink: {
    textDecoration: "none",
    display: "inline-flex",
  },
  buttonContainer: {
    display: "flex",
    gap: "16px",
    marginTop: "24px",
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  primaryButton: {
    background: "#00FF41",
    color: "#000000",
    borderRadius: "8px",
    padding: "12px 24px",
    fontWeight: 600,
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#00CC33",
      transform: "translateY(-2px)",
      boxShadow: "0 10px 20px rgba(0, 255, 65, 0.3)",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#FFFFFF",
    borderRadius: "8px",
    padding: "12px 24px",
    fontWeight: 600,
    textTransform: "none",
    border: "1px solid #2A2A2A",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.1)",
      transform: "translateY(-2px)",
    },
  },
  link: {
    textDecoration: "none",
  },
}));

const closingAboutSentence =
  "When I'm not coding, I like to learn, play raquetball, snowboard, and build random projects. Thank you for viewing my portfolio site!";

function getAboutCopy(company) {
  if (!company) {
    return [
      "A Full-Stack Software Developer with 5 years experience building Frontend components, APIs, and Database logic. I specialize in Typescript, React, Angular, Node.js, Python, AWS services, AI/ML technologies, and SQL & MongoDB Databases and love working in them all. I also am very passionate about cryptocurrency and have built several projects in the space.",
      "I sincerely enjoy my work and collaborating with my team to find solutions to any problem that arises.",
    ];
  }

  return (
    company.aboutCopy || [
      `For the ${company.role} role at ${company.name}, I bring 5+ years of full-stack experience across React, Node.js, Python, databases, cloud services, and production business applications.`,
      "I enjoy working closely with teams to turn messy problems into practical software, and my background across finance, tax, automation, AI, and product engineering helps me connect technical decisions to real business outcomes.",
    ]
  );
}

function getResumeFileName(company) {
  if (!company) {
    return "Resume.pdf";
  }

  if (company.resumeFileName) {
    return company.resumeFileName;
  }

  return `Resume-${company.name.trim().replace(/\s+/g, " ")}.pdf`;
}

function getResumeHref(company) {
  if (!company) {
    return "Resume.pdf";
  }

  return `/resumes/${encodeURIComponent(getResumeFileName(company))}`;
}

export default function About({ company }) {
  const classes = useStyles();
  const aboutCopy = getAboutCopy(company);
  const resumeFileName = getResumeFileName(company);
  const resumeHref = getResumeHref(company);

  return (
    <Grid item xs={12} className={classes.aboutContainer}>
      <Container maxWidth="lg">
        <Typography variant="h4" className={classes.sectionTitle}>
          {company ? "About Berkley" : "About Me"}
        </Typography>
        <Grid container spacing={5}>
          <Grid
            item
            xs={12}
            sm={5}
            md={4}
            className={classes.headshotContainer}
          >
            <img
              src="/images/headshot.jpg"
              className={classes.headshotPic}
              alt="Berkley Olmstead"
            />
          </Grid>

          <Grid item xs={12} sm={7} md={8}>
            <div className={classes.aboutCard}>
              {aboutCopy.map((paragraph) => (
                <Typography key={paragraph} className={classes.bioText}>
                  {paragraph}
                </Typography>
              ))}
              <Typography className={classes.bioText}>
                {closingAboutSentence}
              </Typography>

              <hr className={classes.lineBreak} />

              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.detailLabel}>
                    Location
                  </Typography>
                  <Typography className={classes.detailValue}>
                    Boise, Idaho
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography className={classes.detailLabel}>Email</Typography>
                  <a
                    href="mailto:olms2074@gmail.com"
                    className={classes.emailLink}
                  >
                    olms2074@gmail.com
                  </a>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <div className={classes.iconContainer}>
                    <a
                      href="https://www.linkedin.com/in/berkleyolmstead/"
                      className={classes.contactLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedInIcon className={classes.icon} />
                    </a>
                    <a
                      href="https://github.com/Bolmstead"
                      className={classes.contactLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <GitHubIcon className={classes.icon} />
                    </a>
                  </div>
                </Grid>
              </Grid>

              {!company && (
                <div className={classes.buttonContainer}>
                  <a
                    href={resumeHref}
                    download={resumeFileName}
                    className={classes.link}
                  >
                    <Button
                      variant="contained"
                      className={classes.resumeButton}
                      startIcon={<GetAppIcon />}
                      style={{ width: "200px", marginBottom: "10px" }}
                    >
                      Download Resume
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </Grid>
        </Grid>
      </Container>
    </Grid>
  );
}
